import React, { FunctionComponent } from "react";
import styles from "./Landing.module.scss";
import LandingBlockProps from "./LandingProps.interface";

import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import Button from "components/Button/Button";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import ImageViewer from "components/ImageViewer/ImageViewer";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import useAnchor from "blocks/hooks/useAnchor";

const Landing: FunctionComponent<LandingBlockProps> = (props): JSX.Element => {
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);

    const identifier = useAnchor(props.settings.anchorMenuLabel);

    const logoAspectRatio = getImageAspectRatio(undefined, props.content.image?.umbracoWidth, props.content.image?.umbracoHeight);
    const imageLeftAspectRatio = getImageAspectRatio(undefined, props.content.imageLeft?.umbracoWidth, props.content.imageLeft?.umbracoHeight);
    const imageRightAspectRatio = getImageAspectRatio(undefined, props.content.imageRight?.umbracoWidth, props.content.imageRight?.umbracoHeight);

    const headlineCaseClass = props.settings.headlineCase ? styles[`case_${props.settings.headlineCase}`] : undefined;
    const bannerSizeClass = props.settings.bannerSize ? styles[`size_${props.settings.bannerSize}`] : styles.size_Medium;

    const buttonsOverflowClass = props.content.buttons && props?.content.buttons?.length >= 6 ? styles.button_overflow : "";

    return (
        <section
            className={combineClasses(styles.wrapper, ...wrapperClasses, bannerSizeClass)}
            style={wrapperStyles}
            id={identifier}
        >
            <div className={styles.inner}>
                <div className={styles.content}>
                    {/* Logo */}
                    {props.content.image ? (
                        <div className={combineClasses(styles.logo)}>
                            <ImageViewer
                                className={styles.logo_image}
                                src={props.content.image.umbracoFile.src}
                                extensionType="png"
                                type="contain"
                                aspectRatioX={logoAspectRatio.aspectRatioX}
                                aspectRatioY={logoAspectRatio.aspectRatioY}
                            />
                        </div>
                    ) : null}

                    {/* Headline */}
                    {props.content.headline ? (
                        <DynamicHeading
                            className={combineClasses(styles.headline, headlineCaseClass)}
                            text={props.content.headline}
                            headlineSize={props.settings.headlineSize}
                            headlineWeight={props.settings.headlineWeight}
                        />
                    ) : null}

                    {/* Tagline */}
                    {props.content.tagline ? <span className={styles.tagline}>{props.content.tagline}</span> : null}

                    {/* Buttons */}
                    {props.content.buttons && props.content.buttons.length > 0 ? (
                        <div
                            className={combineClasses(styles.buttons, buttonsOverflowClass)}
                            data-nosnippet
                        >
                            {props.content.buttons.map((button) => (
                                <Button
                                    key={button.contentUdi}
                                    to={button.content.link}
                                    variant={button.content.variant}
                                    {...button.content}
                                >
                                    {button.content.label}
                                </Button>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className={styles.images}>
                    {/* Image Left */}
                    {props.content.imageLeft ? (
                        <div className={combineClasses(styles.image_left_wrapper)}>
                            <ImageViewer
                                className={styles.image_left}
                                src={props.content.imageLeft.umbracoFile.src}
                                extensionType={props.content.imageLeft.umbracoExtension}
                                type="containX"
                                aspectRatioX={imageLeftAspectRatio.aspectRatioX}
                                aspectRatioY={imageLeftAspectRatio.aspectRatioY}
                            />
                        </div>
                    ) : null}

                    {/* Image Right */}
                    {props.content.imageRight ? (
                        <div className={combineClasses(styles.image_right_wrapper)}>
                            <ImageViewer
                                className={styles.image_right}
                                src={props.content.imageRight.umbracoFile.src}
                                extensionType={props.content.imageRight.umbracoExtension}
                                type="containX"
                                aspectRatioX={imageRightAspectRatio.aspectRatioX}
                                aspectRatioY={imageRightAspectRatio.aspectRatioY}
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia
                    key={props.contentUdi}
                    {...props.settings.backgroundMedia[0]}
                />
            ) : null}
        </section>
    );
};

export default Landing;
